/**
 * Styled components for DataCell
 *
 * Optimized to leverage Marriott global design tokens where possible.
 * Custom styles are limited to component-specific requirements:
 * - Elite status gradient backgrounds (brand-specific)
 * - Card image dimensions (specific to card comparison layout)
 * - Icon wrapper sizing
 */

import styled from 'styled-components';
import { baseVariables, toRem } from '@marriott/mi-ui-library';

export const StyledDataCell = styled.div`
  /* Cell wrapper - structural layout */
  .cell-wrapper {
    height: 100%;
  }

  /* Card image sizing - specific to card comparison */
  .card-image {
    width: ${toRem(108)};
  }
  .vertical-card-image {
    height: ${toRem(108)};
    width: unset;
  }

  /* Icon block styling */
  .icon-block {
    span[class^='icon-'] {
      color: ${baseVariables.color['accent10']};
      font-size: ${toRem(36)};
    }
  }

  /* Icon wrapper - circular badge for checkmarks */
  .icon-wrapper {
    border-radius: 50%;
    width: ${toRem(24)};
    height: ${toRem(24)};
  }

  /* Author mode border - editorial view only */
  .cell-border {
    border: ${toRem(1)} ${baseVariables.border.borderStyleDefault} ${baseVariables.color['base20']};
  }

  /* Elite status gradients - brand-specific styling */
  .gold-status {
    background: linear-gradient(
      72deg,
      rgb(185, 147, 98) -4.94%,
      rgb(111, 72, 24) 11.22%,
      rgb(111, 72, 24) 67.24%,
      rgb(120, 84, 39) 87.54%,
      rgb(185, 147, 98) 102.36%
    );
  }

  .platinum-status {
    background: linear-gradient(
      73deg,
      rgb(96, 98, 103) 3.41%,
      rgb(67, 69, 74) 18.01%,
      rgb(67, 69, 74) 78.83%,
      rgb(138, 137, 139) 101.62%,
      rgb(169, 165, 165) 106.62%
    );
  }

  .silver-status {
    background: linear-gradient(
      72deg,
      rgb(112, 117, 141) 0.14%,
      rgb(81, 85, 102) 11.45%,
      rgb(65, 68, 81) 75.73%,
      rgb(112, 117, 141) 93.48%,
      rgb(153, 163, 207) 109.67%
    );
  }

  .silver-status-cell {
    background-color: rgba(109, 113, 133, 1);
  }
  .platinum-status-cell {
    background-color: rgba(94, 95, 97, 1);
  }
  .gold-status-cell {
    background-color: rgba(138, 109, 71, 1);
  }

  .gold-status,
  .platinum-status,
  .silver-status {
    box-shadow: 0 ${toRem(2)} ${toRem(10)} 0 rgba(0, 0, 0, 0.07);
    text-decoration: underline;
    text-decoration-color: ${baseVariables.color.merch50};
  }

  /* Rich text content - remove default paragraph margins */
  .cell-wrapper p {
    margin-bottom: 0;
  }
  .cell-wrapper p:first-child {
    margin-bottom: ${toRem(8)};
  }
  .tertiary-links p {
    margin-bottom: 0;
  }
  /* Points underlined styling - specific to points display */
  .points-underlined {
    font-family: ${baseVariables.font.fontSwissCondensed};
    border-bottom: ${toRem(5)} ${baseVariables.border.borderStyleDefault} ${baseVariables.color.accent10};
    width: fit-content;
    margin: ${toRem(4)} auto ${toRem(0)} auto;
    display: inline-block;
    @media ${baseVariables.mediaQuery.md} {
      margin: ${toRem(8)} auto;
    }

    @media ${baseVariables.mediaQuery.lg} {
      margin: 0;
      display: inline;
    }
  }

  /* Tertiary links styling */
  .tertiary-links a {
    text-decoration: underline;
  }

  /* Card type badges - leveraging global color tokens */
  .bg-personal {
    background-color: ${baseVariables.color['alert20']};
    border-radius: 0 0 0 ${toRem(4)};
    color: ${baseVariables.color['base10']};
  }

  .bg-business {
    background-color: ${baseVariables.color['merch20']};
    border-radius: 0 0 0 ${toRem(4)};
    color: ${baseVariables.color['base20']};
  }
  .points-red {
    color: ${baseVariables.color['alert40']};
  }
  p:has(span .points-red) {
    margin-bottom: ${toRem(8)};
  }
  .m-badge-inline {
    text-transform: capitalize;
    border-radius: ${toRem(4)};
    letter-spacing: normal;
    padding-top: ${toRem(4)};
  }
  .enhanced-comparison-cell {
    border-radius: ${toRem(14)} ${toRem(14)} 0 0;
    .header-text-container {
      p:first-child {
        margin-bottom: ${toRem(8)};
      }
    }
    .gold-status {
      background: linear-gradient(
        180deg,
        ${baseVariables.color['goldSolid']} 0%,
        ${baseVariables.color['base20']} 100%
      );
    }

    .platinum-status {
      background: linear-gradient(
        180deg,
        ${baseVariables.color['platinumSolid']} 0%,
        ${baseVariables.color['base20']} 100%
      );
    }

    .silver-status {
      background: linear-gradient(
        180deg,
        ${baseVariables.color['silverSolid']} 0%,
        ${baseVariables.color['base20']} 100%
      );
    }
    .gold-status,
    .platinum-status,
    .silver-status {
      border-radius: 0;
      box-shadow: none;
      text-decoration: none;
    }

    .bg-personal {
      background-color: ${baseVariables.color['neutral10']};
      border-radius: ${toRem(4)};
      color: ${baseVariables.color['base10']};
      text-transform: capitalize;
      span {
        color: ${baseVariables.color['base10']};
      }
    }

    .bg-business {
      background-color: ${baseVariables.color['base10']};
      border-radius: ${toRem(4)};
      color: ${baseVariables.color['base20']};
      text-transform: capitalize;
    }
  }
  .btn-label {
    white-space: normal;
  }
`;
